import React, { useEffect, useState } from "react";
import { ArrowLeft, CalendarDays } from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import CustomCalendar from "../../../components/CustomBookingCalendar";
import { useLocation, useNavigate } from "react-router-dom";
import TaxiSummaryCard from "../../../components/customer/TaxiSummaryCard";
import BookingCartSummary from "../../../components/customer/BookingCartSummary";
import { useAlert } from "../../../components/customer/AlertContext";
import {
  useGetAvailableSlotsMutation,
  useGetOffersCalendarMutation,
  useGetWorkingDaysByServiceIdQuery,
} from "../../../redux/actions/Customer/serviceCustomer";
import { useAddServiceToCartMutation } from "../../../redux/actions/Customer/cartCustomer";
import { AddOn } from "../../../types/Response/CustomerResponse";

const BookingDateScreen: React.FC = () => {
  const { showConfirm } = useAlert();
  const location = useLocation();
  const navigate = useNavigate();

  const serviceId = location.state?.serviceId || 24;
  const isTaxiService = serviceId === 27;

  const selectedPackage = location.state?.selectedPackage;
  const packageId = isTaxiService
    ? undefined
    : location.state?.packageId || selectedPackage?.packageId;
  const serviceType = location.state?.serviceType || "grooming";
  const bookingData = location.state?.bookingData;

  const selectedAddOns: AddOn[] = location.state?.selectedAddOns || [];
  const calculatedTaxiPrice =
    bookingData?.pricing?.totalPrice ??
    bookingData?.pricing?.data?.totalPrice ??
    location.state?.totalPrice ??
    0;

  const totalPrice = isTaxiService
    ? calculatedTaxiPrice
    : location.state?.totalPrice || 0;

  const [finalSlot, setFinalSlot] = useState<{
    date: Date;
    time: string;
    startTime?: string;
    endTime?: string;
  } | null>(null);

  const [getAvailableSlots, { data: availableSlots }] =
    useGetAvailableSlotsMutation();
  const [getOffersCalendar, { data: offers }] =
    useGetOffersCalendarMutation();
  const [addServiceToCart, { isLoading: isAddingToCart }] =
    useAddServiceToCartMutation();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const startDate = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  );
  const endDate = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  );

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const { data: workingDays } = useGetWorkingDaysByServiceIdQuery(serviceId, {
    skip: !serviceId,
  });

  useEffect(() => {
    if (!serviceId || isTaxiService || !packageId) return;

    getOffersCalendar({
      ServiceId: serviceId,
      ServicePackageId: packageId,
      StartDate: formatDate(startDate),
      EndDate: formatDate(endDate),
    }).catch((err) => console.error("Offers Fetch Error:", err));
  }, [serviceId, packageId, isTaxiService, currentMonth, getOffersCalendar]);

  useEffect(() => {
    if (!serviceId) return;

    const today = new Date();
    const bookingDate = formatDate(today);

    getAvailableSlots({
      serviceId,
      ...(isTaxiService ? {} : { servicePackageId: packageId }),
      bookingDate,
    });
  }, [serviceId, packageId, isTaxiService, getAvailableSlots]);

  const handleDateTimeSelection = (date: Date, timeSlot: string) => {
    const parts = timeSlot.split(" - ");
    const startTime = parts[0]?.trim() || "09:00:00";
    const endTime = parts[1]?.trim() || "10:00:00";

    setFinalSlot({
      date,
      time: timeSlot,
      startTime: startTime.length === 5 ? `${startTime}:00` : startTime,
      endTime: endTime.length === 5 ? `${endTime}:00` : endTime,
    });
  };

  const handleDateSelect = async (date: Date) => {
    const bookingDate = formatDate(date);
    await getAvailableSlots({
      serviceId,
      ...(isTaxiService ? {} : { servicePackageId: packageId }),
      bookingDate,
    });
  };

  const handleFinalizeBooking = async () => {
    if (!finalSlot) return;

    const savedPet = isTaxiService
      ? bookingData?.pet
      : selectedPackage?.savedPetInfo;

    let serviceDataJson = "";

    if (isTaxiService && bookingData) {
      serviceDataJson = JSON.stringify({
        pickup: {
          address: bookingData.pickup?.address,
          addressLine2: bookingData.pickup?.addressLine2 || "",
          city: bookingData.pickup?.city,
          state: bookingData.pickup?.state,
          country: bookingData.pickup?.country,
          postalCode: bookingData.pickup?.postalCode,
          latitude: bookingData.pickup?.latitude,
          longitude: bookingData.pickup?.longitude,
        },
        drop: {
          address: bookingData.drop?.address,
          addressLine2: bookingData.drop?.addressLine2 || "",
          city: bookingData.drop?.city,
          state: bookingData.drop?.state,
          country: bookingData.drop?.country,
          postalCode: bookingData.drop?.postalCode,
          latitude: bookingData.drop?.latitude,
          longitude: bookingData.drop?.longitude,
        },
        pricing: bookingData.pricing,
        totalPrice: totalPrice,
      });
    } else {
      serviceDataJson = JSON.stringify({
        selectedAddOns: selectedAddOns.map((addon) => ({
          id: addon.addOnId,
          servicePackageId: packageId,
          name: addon.name,
          description: addon.description,
          imageUrl: addon.imageUrl,
          price: addon.price,
        })),
      });
    }

    const payload = {
      serviceId,
      ...(isTaxiService
        ? {}
        : {
          servicePackageId: packageId,
          servicePackagePricingId: selectedPackage?.pricing?.pricingId || 0,
        }),
      petProfileId: savedPet?.id || 1,
      bookingDate: formatDate(finalSlot.date),
      startTime: finalSlot.startTime || "09:00:00",
      endTime: finalSlot.endTime || "10:00:00",
      price: totalPrice,
      petName: savedPet?.name || savedPet?.petName || "Pet",
      petType: isTaxiService
        ? bookingData?.petType || "Dog"
        : savedPet?.type || "Dog",
      petBreed: isTaxiService
        ? bookingData?.breed || "Standard"
        : savedPet?.breed || "Standard",
      petSize: isTaxiService
        ? bookingData?.weight || "Standard"
        : selectedPackage?.pricing?.size || "Medium",
      notes: isTaxiService
        ? `Pet Taxi Transfer from ${bookingData?.pickup?.city || "Pickup"} (${bookingData?.pickup?.addressLine2 || ""}) to ${bookingData?.drop?.city || "Dropoff"
        } (${bookingData?.drop?.addressLine2 || ""})`
        : "Customer requested standard service with optional add-ons.",
      serviceDataJson,
    };

    try {
      const success = await addServiceToCart(payload as any).unwrap();
      if (success) {
        navigate("/cartpage/customercartpage");
      }
    } catch (error) {
      console.error("Failed to add service to cart:", error);
      showConfirm({
        title: "Error",
        message: "Could not add item to cart. Please try again.",
        type: "error",
        confirmText: "Close",
        showCancel: false,
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10 text-left">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-logoHeading hover:text-mainBrandColor transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
          <span className="font-medium">Back</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* CALENDAR PANEL */}
        <div className="lg:col-span-7 xl:col-span-8 bg-white rounded-[2rem] border border-[#ECE7DF] shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-50 flex items-center gap-3">
            <div className="p-2 bg-mainBrandColor/10 text-mainBrandColor rounded-xl">
              <CalendarDays size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-logoHeading">
                Select Appointment Time
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Pick a convenient date and timing slot below
              </p>
            </div>
          </div>

          <CustomCalendar
            serviceType={serviceType}
            slots={availableSlots || []}
            workingDays={workingDays || []}
            offers={offers || []}
            currentMonth={currentMonth}
            onMonthChange={setCurrentMonth}
            onDateSelect={handleDateSelect}
            onDateTimeSelect={handleDateTimeSelection}

          />
        </div>

        {/* STICKY CART SUMMARY PANEL */}
        <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-6 space-y-4">
          {serviceType === "grooming" && selectedPackage && (
            <BookingCartSummary
              packageItem={selectedPackage}
              addOns={selectedAddOns}
              totalPrice={totalPrice}
            />
          )}

          {(serviceType === "taxi" || serviceType === "mobilegrooming") && bookingData && (
            <TaxiSummaryCard
              petName={bookingData.pet?.name || bookingData.pet?.petName}
              petType={bookingData.petType}
              breed={bookingData.breed}
              weight={bookingData.weight}
              pickupCity={bookingData.pickup?.city}
              dropCity={bookingData.drop?.city}
              pickupAddress={`${bookingData.pickup?.addressLine2 ? bookingData.pickup.addressLine2 + ", " : ""}${bookingData.pickup?.address}`}
              dropAddress={`${bookingData.drop?.addressLine2 ? bookingData.drop.addressLine2 + ", " : ""}${bookingData.drop?.address}`}
              totalPrice={totalPrice}
              pricing={bookingData.pricing}
            />
          )}

          <div className="flex justify-center">
            <CommonButton
              text={
                isAddingToCart ? "Adding to Cart..." : "Confirm Appointment"
              }
              disabled={!finalSlot || isAddingToCart}
              onClick={handleFinalizeBooking}
              className="w-full cursor-pointer"
              variant="filled"
              rounded="md"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDateScreen;